import { v4 as uuid } from 'uuid';
import { AxiosResponse } from 'axios';
import IndigoApiError, {
    isAxiosError,
    isIndigoApiError,
} from 'communication/IndigoApiError';

export type ApiRequestVerb = 'get' | 'post' | 'put' | 'delete';

export interface ApiResponseInfo {
    status: number;
    receivedTime: Date;
    data: unknown;
}

export interface ApiErrorResponseInfo {
    receivedTime: Date;
    status?: number;
    message?: string;
    data?: unknown;
}

export interface ApiRequestInfo {
    id: string;
    correlationId: string;
    verb: ApiRequestVerb;
    url: string;
    body?: unknown;
    headers?: unknown;
    sentTime: Date;
    response?: ApiResponseInfo | ApiErrorResponseInfo;
}

export function buildRequestInfo(
    verb: ApiRequestVerb,
    url: string,
    correlationId: string,
    body?: unknown,
    headers?: unknown
): ApiRequestInfo {
    return {
        id: uuid(),
        correlationId,
        verb,
        url,
        body,
        headers,
        sentTime: new Date(),
    };
}

export function buildResponseInfo<TResponseData>(
    response: AxiosResponse<TResponseData>
): ApiResponseInfo {
    return {
        status: response.status,
        receivedTime: new Date(),
        data: response.data,
    };
}

export function buildErrorResponseInfo(
    error: IndigoApiError | AxiosResponse | unknown
): ApiErrorResponseInfo {
    if (isIndigoApiError(error)) {
        return {
            receivedTime: new Date(),
            status: error.status,
            message: error.message,
            data: error.data,
        };
    }

    if (isAxiosError(error)) {
        return {
            receivedTime: new Date(),
            status: error.response?.status,
            data: error.response?.data,
            message: error.response?.data?.message
                ? error.response?.data?.message
                : error.message,
        };
    }

    return {
        receivedTime: new Date(),
        data: error,
    };
}

export type ApiOnPendingCallback = (requestInfo: ApiRequestInfo) => void;
export type ApiOnResponseCallback = (
    requestInfoId: string,
    responseInfo: ApiResponseInfo
) => void;
export type ApiOnErrorResponseCallback = (
    requestInfoId: string,
    responseErrorInfo: ApiErrorResponseInfo
) => void;

export interface ApiRequestOptions {
    onPending: ApiOnPendingCallback;
    onResponse: ApiOnResponseCallback;
    onError: ApiOnErrorResponseCallback;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    headers?: any;
}

export const nullApiRequestOptions: ApiRequestOptions = {
    onPending: () => {
        return;
    },
    onResponse: () => {
        return;
    },
    onError: () => {
        return;
    },
};
